import { useEffect, useMemo, useState } from "react";

import BuildingSkyscraper from "@assets/icons/building-skyscraper.svg";

import { IPeopleName } from "@interfaces/people.interface";
import { normalizeLogoUrl } from "@utils/logo.util";
import { getUserInitials } from "@utils/users.util";

export enum CompanyAvatarSizes {
  SMALL,
  BIG,
}

export interface ICompanyAvatarProps {
  size: CompanyAvatarSizes;
  logoUrl?: string | null;
  userProps?: IPeopleName;
  classNames?: string;
  canShowCompanyLogo?: boolean;
  showInitialsByDefault?: boolean;
}

export const CompanyAvatar = ({
  size,
  logoUrl: rawLogoUrl,
  userProps,
  classNames,
  canShowCompanyLogo = true,
  showInitialsByDefault = false,
}: ICompanyAvatarProps) => {
  // TODO: remove normalizeLogoUrl once the Scout Agent index is
  // re-built with the new URL format from BE.
  const logoUrl = normalizeLogoUrl(rawLogoUrl);
  const [imageFailed, setImageFailed] = useState(false);

  // Reset failure state when a new logo URL arrives, so retries get a fresh chance.
  useEffect(() => {
    setImageFailed(false);
  }, [logoUrl]);

  const renderStyleBySize = useMemo(() => {
    if (size === CompanyAvatarSizes.SMALL) return "w-4 h-4 rounded text-[10px]";

    if (size === CompanyAvatarSizes.BIG) {
      if (!logoUrl || !canShowCompanyLogo || imageFailed)
        return "w-5 h-5 rounded-full border border-gray-200 bg-[#F9F9FB] text-[10px]";
      if (userProps) return "w-5 h-5 rounded-lg border border-gray-200 text-[8px]";
    }

    return "";
  }, [logoUrl, size, userProps, canShowCompanyLogo, imageFailed]);

  const initials = useMemo(() => {
    if (!userProps) return undefined;
    const result = getUserInitials(userProps);
    return result.length > 0 ? result : "-";
  }, [userProps]);

  const hasValidLogo = Boolean(logoUrl && logoUrl.length > 0 && canShowCompanyLogo && !imageFailed);

  return (
    <div
      className={`relative flex shrink-0 items-center justify-center overflow-hidden bg-white ${renderStyleBySize} ${classNames ?? ""}${
        ((!logoUrl || imageFailed) && initials) || !canShowCompanyLogo
          ? " !border-solid border-[#E5E7EB]"
          : ""
      }`}
    >
      {hasValidLogo ? (
        <img
          src={logoUrl as string}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : initials || showInitialsByDefault ? (
        <span className="flex h-full w-full items-center justify-center bg-slate-100 text-[#9CA1AF]">
          {initials}
        </span>
      ) : (
        <BuildingSkyscraper width={20} height={20} className="flex-shrink-0" />
      )}
    </div>
  );
};
